import { getCurrentInstance, nextTick, onBeforeUnmount, onMounted, watch, type Ref } from 'vue';

export interface UseInfiniteScrollObserverOptions {
  enabled: Ref<boolean>;
  loadingMore: Ref<boolean>;
  hasMore: Ref<boolean>;
  onLoadMore: () => void | Promise<void>;
  triggerId?: string;
  rootMargin?: string;
  minIntervalMs?: number;
}

export function useInfiniteScrollObserver(options: UseInfiniteScrollObserverOptions): void {
  const {
    enabled,
    loadingMore,
    hasMore,
    onLoadMore,
    triggerId = 'infinite-scroll-trigger',
    rootMargin = '200px',
    minIntervalMs = 500,
  } = options;

  let observer: IntersectionObserver | null = null;
  let triggerObserver: MutationObserver | null = null;
  let lastLoadTime = 0;

  const disconnectTriggerObserver = () => {
    triggerObserver?.disconnect();
    triggerObserver = null;
  };

  const observeTrigger = (doc: Document) => {
    if (!observer) return false;

    const el = doc.getElementById(triggerId);
    if (!el) return false;

    observer.observe(el);
    disconnectTriggerObserver();

    return true;
  };

  const disconnect = () => {
    disconnectTriggerObserver();

    if (!observer) return;
    try {
      observer.disconnect?.();
    } catch (error) {
      console.warn('Error disconnecting IntersectionObserver:', error);
    }
    observer = null;
  };

  const setup = () => {
    disconnect();
    if (!enabled.value) return;

    observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting) return;
        if (!hasMore.value || loadingMore.value) return;

        const now = Date.now();
        if (now - lastLoadTime < minIntervalMs) return;

        lastLoadTime = now;
        void onLoadMore();
      },
      { rootMargin }
    );

    if (observeTrigger(document)) return;

    const root = document.body ?? document.documentElement;
    if (!root) return;
    // The callback can run after the test environment removes the global `document`, when a
    // component was mounted and never unmounted; the observed node keeps its own document.
    const doc = root.ownerDocument;

    triggerObserver = new MutationObserver(() => {
      if (!enabled.value) {
        disconnectTriggerObserver();
        return;
      }

      observeTrigger(doc);
    });

    triggerObserver.observe(root, { childList: true, subtree: true });
    observeTrigger(doc);
  };

  if (getCurrentInstance()) {
    onMounted(async () => {
      await nextTick();
      setup();
    });

    onBeforeUnmount(() => {
      disconnect();
    });
  }

  watch(enabled, async (isEnabled) => {
    if (isEnabled) {
      await nextTick();
      setup();
    } else {
      disconnect();
    }
  });
}
